import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Bot, Check, CheckCircle2, Download, ExternalLink, Layers3, Sparkles, UserCheck } from 'lucide-react';
import { getLeadSummary, track, type LeadSummary } from '../../api/client';
import { AppHeader } from '../../components/AppHeader';
import { Button } from '../../components/Button';
import type { StoredResult } from '../../storage/results';
import { telegram } from '../../telegram/bridge';
import { useI18n } from '../../i18n/I18nProvider';

const developerSummary = (sessionId: string): LeadSummary => ({
  sessionId,
  username: 'demo_client',
  firstName: 'Alex',
  requestType: 'New tattoo',
  style: 'Blackwork',
  size: '10–20 cm',
  placement: 'Arm',
  timing: 'This month',
  references: 2,
  tryonCompleted: true,
  source: 'telegram',
  campaign: 'cold_tg_001',
  exportUnlocked: true,
});

interface RevealScreenProps {
  sessionId: string;
  developerMode?: boolean;
  onSave: (blob: Blob) => void;
  onRequest: () => void;
  onBack: () => void;
}

export function RevealScreen({ sessionId, developerMode = false, onSave, onRequest, onBack }: RevealScreenProps) {
  const { t } = useI18n();
  const [summary, setSummary] = useState<LeadSummary | null>(null);
  const [result, setResult] = useState<StoredResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void Promise.all([
      getLeadSummary(sessionId).catch((cause) => {
        if (developerMode) return developerSummary(sessionId);
        throw cause;
      }),
      Promise.resolve(null as StoredResult | null),
    ]).then(([lead, stored]) => {
      if (!active) return;
      setSummary(lead);
      setResult(stored);
      track('admin_demo_viewed', sessionId);
      track('b2b_offer_viewed', sessionId);
    }).catch((cause) => {
      if (active) setError(cause instanceof Error ? cause.message : t('reveal.loadError'));
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [developerMode, sessionId, t]);

  if (loading) return <main className="screen state-screen"><span className="spinner" /><h1>{t('reveal.preparing')}</h1></main>;
  if (error || !summary) return <main className="screen state-screen"><UserCheck /><h1>{t('reveal.unavailable')}</h1><p>{error || t('reveal.openQuiz')}</p></main>;

  const fields = [
    [t('reveal.telegramUser'), summary.username ? `@${summary.username}` : summary.firstName || t('reveal.verifiedUser')],
    [t('reveal.request'), summary.requestType], [t('reveal.style'), summary.style], [t('reveal.size'), summary.size], [t('reveal.placement'), summary.placement],
    [t('reveal.timing'), summary.timing], [t('reveal.references'), String(summary.references)], [t('reveal.tryonCompleted'), summary.tryonCompleted ? t('reveal.yes') : t('reveal.no')],
    [t('reveal.source'), summary.source], [t('reveal.campaign'), summary.campaign || '—'], [t('reveal.sessionId'), summary.sessionId.slice(0, 18)],
  ];

  return (
    <main className="screen reveal-screen">
      <AppHeader title={t('reveal.title')} action={!telegram.isAvailable() ? <button className="icon-button" onClick={onBack} aria-label={t('common.back')}><ArrowLeft /></button> : undefined} />
      <div className="screen-content reveal-content">
        <section className="perspective-switch">
          <div className="perspective-switch__icon"><CheckCircle2 /></div>
          <p className="eyebrow">{t('reveal.customerExperience')}</p>
          <h1>{t('reveal.smoothPath')}</h1>
          <div className="flow-strip" aria-label={t('reveal.completedJourney')}><span>Try-On</span><ArrowRight /><span>{t('reveal.quiz')}</span><ArrowRight /><span>{t('reveal.request')}</span><Check /></div>
        </section>

        <section className="admin-reveal">
          <div className="admin-reveal__heading"><p className="eyebrow">{t('reveal.studioReceives')}</p><h2>{t('reveal.qualifiedLead')}</h2></div>
          <article className="lead-card">
            <header><div className="lead-card__status"><span /> {t('reveal.newLead')}</div><time>{t('reveal.justNow')}</time></header>
            <div className="lead-card__user"><div><UserCheck /></div><span><strong>{summary.firstName || t('reveal.telegramCustomer')}</strong><small>{summary.username ? `@${summary.username}` : t('reveal.verifiedTelegram')}</small></span></div>
            <dl>{fields.slice(1).map(([label, value]) => <div key={label}><dt>{label}</dt><dd className={label === t('reveal.tryonCompleted') ? 'is-positive' : ''}>{value}</dd></div>)}</dl>
          </article>
        </section>

        {summary.exportUnlocked && <section className="clean-result-card">
          <div><Download /><span><strong>{t('reveal.cleanUnlocked')}</strong><small>{result ? t('reveal.savedLocal') : t('reveal.returnDevice')}</small></span></div>
          <Button variant="secondary" onClick={() => result && onSave(result.clean)} disabled={!result}>{t('reveal.saveResult')}</Button>
        </section>}

        <section className="offer-section">
          <p className="eyebrow">{t('reveal.bringExperience')}</p>
          <h2>{t('reveal.products')}</h2>
          <div className="product-list">
            <article><div><Sparkles /></div><span><strong>{t('b2b.tryon')}</strong><p>{t('reveal.tryonProduct')}</p></span></article>
            <article><div><Bot /></div><span><strong>{t('reveal.quizProduct')}</strong><p>{t('reveal.quizProductCopy')}</p></span></article>
            <article className="is-recommended"><span className="recommended-badge">{t('reveal.recommended')}</span><div><Layers3 /></div><span><strong>{t('reveal.fullFunnel')}</strong><p>{t('reveal.fullFunnelCopy')}</p></span></article>
          </div>
          <Button block icon={<ExternalLink />} onClick={() => { track('b2b_cta_clicked', sessionId); onRequest(); }}>{t('reveal.getStudio')}</Button>
        </section>
      </div>
    </main>
  );
}
